import React, { useState, useRef, useCallback, useEffect } from 'react';
import { translate } from '../Services/translation';

const NavigationItem = ({
    links = [],
    contentLink = null,
    renderAddiontalLinks = true,
    level = 0,
    parentMenuOpen = false,
    isSubMenuOpen = false,
    onCloseParent,
    isInMegaMenuColumns = false
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const menuRef = useRef(null);
    const linkRef = useRef(null);

    const mobileWidth = 1024;
    const isMobile = window?.innerWidth <= mobileWidth;
    const bannerTitle = contentLink?.bannerTitle ?? '';
    const bannerImage = contentLink?.bannerImageUrl ?? '';
    const bannerTitleColor = contentLink?.bannerTitleColor ?? '';
    const bannerCTAColorSettings = contentLink?.bannerCTAColorSettings ?? '';
    const bannerCTAPlacement = contentLink?.bannerCTAPlacement ?? '';
    const bannerCTA = contentLink?.bannerCTA ?? '';
    const showMobileOnly = contentLink?.showMobileOnly;
    const showDesktopOnly = contentLink?.showDesktopOnly;

    const hasRenderableLink = (link) => {
        if ((link?.showMobileOnly === true && !isMobile) ||
            (link?.showDesktopOnly === true && isMobile)) {
            return false;
        }

        const hasName = typeof link?.name === 'string' && link.name.trim().length > 0;
        const hasRenderableChildren = (link?.links ?? []).some(hasRenderableLink);
        const hasBanner = Boolean(link?.bannerTitle || link?.bannerImageUrl);

        return hasName || hasRenderableChildren || hasBanner;
    };

    const visibleLinks = (!isMobile && isInMegaMenuColumns && level >= 3 ? [] : links)
        .filter(hasRenderableLink);
    const hasChildren = visibleLinks.length > 0;
    const hideMegaMenuColumn = (showMobileOnly === true && !isMobile) ||
        (showDesktopOnly === true && isMobile);

    const getTabIndex = () => {
        return isMenuOpen || parentMenuOpen || isSubMenuOpen ? 0 : -1;
    };

    const closeSelfAndParents = useCallback(() => {
        setIsMenuOpen(false);
        setIsFocused(false);

        if (onCloseParent) {
            onCloseParent();
        }
    }, [onCloseParent]);

    useEffect(() => {
        const body = document.querySelector(".js-body");
        if (isMenuOpen || parentMenuOpen || isSubMenuOpen) {
            body.classList.add("disable-scroll--mobile");
        }
        else {
            body.classList.remove("disable-scroll--mobile");
        }
    }, [isMenuOpen, parentMenuOpen, isSubMenuOpen]);

    useEffect(() => {
        const navigationRows = document.querySelectorAll('.navigation-row');
        navigationRows.forEach(row => {
            const navigationBannerDesktop = row.querySelector('.navigation-banner-desktop');
            if (navigationBannerDesktop) {
                row.classList.add('contain-banner');
            }
        });
    }, []);

    useEffect(() => {
        if (!isMenuOpen || !isMobile) return;
        const menu = menuRef.current;
        if (!menu) return;

        const handleKeyDown = (e) => {
            if (e.key !== 'Tab') return;

            const closeBtn = menu.querySelector(':scope .navbar__icon--close');
            let rest;
            if (level === 0) {
                rest = Array.from(menu.querySelectorAll('a.navbar__link.level-1'))
                    .filter(el => !el.hasAttribute('disabled') && el.tabIndex >= 0);
            } else {
                rest = Array.from(menu.querySelectorAll('a,button,[tabindex]:not([tabindex="-1"])'))
                    .filter(el => !el.hasAttribute('disabled') && el.tabIndex >= 0 && !el.classList.contains('navbar__icon--close'));
            }
            const focusable = closeBtn ? [closeBtn, ...rest] : rest;

            if (!focusable.length) return;

            const first = focusable[0], last = focusable[focusable.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        menu.addEventListener('keydown', handleKeyDown);
        return () => menu.removeEventListener('keydown', handleKeyDown);
    }, [isMenuOpen, level]);

    const focusFirstTopLinkIfNeeded = () => {
        if (isMobile && level === 1) {
            const firstTopLink = document.querySelector('.navbar__menu.level-0 .navbar__link.level-1');
            firstTopLink?.focus();
        }
    };

    const handleEnterSpace = (e, action) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        action();
    };

    const toggleMenu = (e) => {
        e.preventDefault();
        setIsMenuOpen(prev => !prev);
        setIsFocused(true);
    };

    const handleKeyDown = (e) => {
        const { key } = e;

        if (key === 'Enter') {
            if (contentLink?.url || getColumnHeaderUrl()) {
                return;
            }

            e.preventDefault();
            if (hasChildren) {
                setIsMenuOpen(prev => !prev);
                setIsFocused(true);
            }
        }

        if (key === ' ') {
            e.preventDefault();

            if (hasChildren) {
                setIsMenuOpen(prev => !prev);
                setIsFocused(true);
            }
        }

        if (key === 'Escape') {
            e.preventDefault();

            if (isMobile) {
                const rootMenu = document.querySelector('.navbar__menu.level-0');
                if (rootMenu) {
                    rootMenu.classList.remove('navbar__menu--show', 'show-desktop-menu');
                    rootMenu.previousElementSibling.className =
                        "navbar__link--block navbar__icon--menu navbar__icon";
                }

                setIsMenuOpen(false);
                setIsFocused(false);
                return;
            }

            if (level === 1) {
                setIsMenuOpen(false);
                setIsFocused(false);
                return;
            }

            closeSelfAndParents();
        }
    };

    const handleBlur = (e) => {
        if (isMobile) {
            return;
        }

        const menu = menuRef.current;
        if (!menu) return;

        if (menu.contains(e.relatedTarget)) {
            return;
        }

        setIsFocused(false);
        setIsMenuOpen(false);
    };

    const renderMobileBanner = () => (
        <a href={getBannerUrl()} tabIndex={-1} aria-hidden={!getBannerUrl() ? true : false} aria-label={bannerTitle ?? translate('general.arialabel.goto') + { bannerTitle }}>
            <div className="navigation-banner-mobile">
                <div className="navigation-banner-mobile-inner-wrapper">
                    <img src={bannerImage} className="column-banner-image" alt="" />
                    <h3 className={`banner-title-color-${bannerTitleColor}`}>{bannerTitle}</h3>
                    {bannerCTA && (
                        <div className={`banner-cta banner-cta-colorsettings-${bannerCTAColorSettings} banner-cta-placement-${bannerCTAPlacement}`}>
                            <button className="banner-cta-button">{bannerCTA}</button>
                        </div>
                    )}
                </div>
            </div>
        </a>
    );

    const renderDesktopBanner = () => (
        <a className="navigation-banner-desktop" href={getBannerUrl()} aria-label={bannerTitle ?? translate('general.arialabel.goto') + { bannerTitle }} aria-hidden={!getBannerUrl() ? true : false} tabIndex={-1}>
            <div>
                <img src={bannerImage} className="column-banner-image" alt="" />
                <h3 className={`banner-title-color-${bannerTitleColor}`}>{bannerTitle}</h3>
                {bannerCTA && (
                    <div className={`banner-cta banner-cta-colorsettings-${bannerCTAColorSettings} banner-cta-placement-${bannerCTAPlacement}`}>
                        <button className="banner-cta-button" tabIndex={getTabIndex()}>{bannerCTA}</button>
                    </div>
                )}
            </div>
        </a>
    );

    const getColumnHeaderUrl = () => {
        const categoryUrl = contentLink?.useCategoryLinkForColumnHeader
            ? contentLink.categoryLinkToColumnHeader
            : null;

        const pageUrl = !contentLink?.useCategoryLinkForColumnHeader
            ? contentLink?.pageLinkToColumnHeader
            : null;

        return categoryUrl || pageUrl || '#';
    };

    const getBannerUrl = () => {
        const categoryUrl = contentLink?.useCategoryLinkForBanner
            ? contentLink.bannerLinkToCategory
            : null;

        const pageUrl = !contentLink?.useCategoryLinkForBanner
            ? contentLink?.bannerLinkToPage
            : null;

        return categoryUrl || pageUrl || '#';
    };

    const hasNameOrChildren = (link) => hasRenderableLink(link);
    const hideForMobile = (link) => (!link.name || link.name === '') && (!link.bannerImageUrl || link.bannerImageUrl === '');
    const hideSubSubLinksClass = contentLink && contentLink.showSubSubLinks === false ? 'hide-third-level' : '';
    const navbarStateIcon = `navbar__icon${isMenuOpen ? '--hidden' : '--menu'}`;

    if (hideMegaMenuColumn) {
        return null;
    }

    return (
        <>
            {!contentLink ? (
                <a
                    className={"navbar__link--block " + navbarStateIcon + " navbar__icon"}
                    onClick={(e) => toggleMenu(e, true)}
                    rel="nofollow"
                    href="#"
                    title={translate('general.menu') || 'menu'}
                    onKeyDown={handleKeyDown}
                    tabIndex={isMenuOpen ? -1 : 0}
                />
            ) : (
                <>
                    <a
                        className={"navbar__link level-" + level +
                            (contentLink.url?.length > 1 ? " column-header-url" : "") +
                            (contentLink.isSelected ? " navbar__link--selected" : "") +
                            (hasChildren ? " has-children" : "") +
                            (contentLink?.attributes?.cssValue ? " " + contentLink.attributes.cssValue : "") +
                            (contentLink?.attributesColumnHeader?.cssValue ? " " + contentLink.attributesColumnHeader.cssValue : "") +
                            (bannerTitle ? " hide-anchor-tag" : "")
                        }
                        href={contentLink.url || getColumnHeaderUrl()}
                        tabIndex={!isMobile && level == 1 || !isMobile && isSubMenuOpen ? 0 : level == 1 ? 0 : isMenuOpen || parentMenuOpen ? 0 : -1}
                        aria-haspopup={hasChildren ? 'true' : undefined}
                        aria-expanded={hasChildren ? isMenuOpen : undefined}
                        aria-controls={hasChildren ? `menu-${contentLink.name?.replace(/\s+/g, '-')}` : undefined}
                        aria-hidden={bannerTitle || contentLink.name == undefined ? true : false}
                        aria-label={contentLink.name !== undefined ? translate('general.arialabel.goto') + contentLink.name : ""}
                        onKeyDown={handleKeyDown}
                        onClick={(e) => {
                            if (contentLink?.url || getColumnHeaderUrl()) {
                                return;
                            }
                            if (hasChildren) {
                                e.preventDefault();
                                setIsMenuOpen(prev => !prev);
                                setIsFocused(true);
                            }
                        }}

                        dangerouslySetInnerHTML={{ __html: contentLink.name }}
                        ref={linkRef}
                        onFocus={() => setIsFocused(true)}
                    />

                    {!bannerTitle && hasChildren && (
                        <i
                            className="navbar__icon--caret-right navbar__icon navbar__icon--open"
                            onClick={(e) => {
                                e.preventDefault();
                                setIsMenuOpen((prev) => !prev);
                                setIsFocused(true);
                            }}
                            onKeyDown={handleKeyDown}
                            tabIndex={getTabIndex()}
                            aria-hidden="true"
                        />
                    )}
                </>
            )}

            {bannerTitle && renderMobileBanner()}

            {!bannerTitle && hasChildren && (

                <div
                    className={
                        "navbar__menu " +
                        hideSubSubLinksClass +
                        (level >= 1 && isMenuOpen && isFocused ? " isFocused" : "") +
                        " level-" + level +
                        (isMenuOpen && isFocused ? " show-desktop-menu navbar__menu--show" : "")
                    }
                    ref={menuRef}
                    id={contentLink ? `menu-${contentLink.name?.replace(/\s+/g, '-')}` : undefined}
                    onFocus={() => setIsFocused(true)}
                    onBlur={handleBlur}
                >
                    <a
                        className={"navbar__link--block navbar__icon--close navbar__icon"}
                        onClick={(e) => toggleMenu(e, true)}
                        rel="nofollow"
                        href="#"
                        title={translate('general.menu') || 'menu'}
                        onKeyDown={handleKeyDown}
                        tabIndex={isMobile ? 0 : -1}
                        role="button"
                    />
                    <div className="navbar__menu-header">
                        {level >= 1 && (
                            <>
                                <i
                                    className="navbar__icon--caret-left navbar__icon"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        toggleMenu(e);
                                        focusFirstTopLinkIfNeeded();
                                    }}
                                    tabIndex={isMenuOpen && isFocused ? 0 : -1}
                                    role="button"
                                    aria-label={translate('general.back')}
                                    onKeyDown={(e) =>
                                        handleEnterSpace(e, () => {
                                            toggleMenu(e);
                                            focusFirstTopLinkIfNeeded();
                                        })
                                    }
                                />
                                <span
                                    className="navbar__title"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        toggleMenu(e);
                                        focusFirstTopLinkIfNeeded();
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: contentLink.name
                                    }}
                                    tabIndex={isMenuOpen && isFocused ? 0 : -1}
                                    role="button"
                                    aria-label={contentLink.name}
                                    onKeyDown={(e) =>
                                        handleEnterSpace(e, () => {
                                            toggleMenu(e);
                                            focusFirstTopLinkIfNeeded();
                                        })
                                    }
                                />
                            </>
                        )}
                    </div>

                    <ul className={"navbar__menu-links " + (contentLink?.showMegaMenuColumns ? "navigation-row" : "navigation-col")}>
                        {visibleLinks.map(
                            (link, index) =>

                                hasNameOrChildren(link) && (
                                    <li
                                        key={index}
                                        className={
                                            "navbar__item " +
                                            (contentLink?.fontWeightBold ? "font-bold" : "font-regular") +
                                            (hideForMobile(link) ? "" : "")
                                        }
                                    >
                                        <NavigationItem
                                            links={link.links}
                                            contentLink={link}
                                            renderAddiontalLinks={false}
                                            level={level + 1}
                                            parentMenuOpen={isMenuOpen}
                                            isSubMenuOpen={parentMenuOpen}
                                            onCloseParent={closeSelfAndParents}
                                            isInMegaMenuColumns={isInMegaMenuColumns || !!contentLink?.showMegaMenuColumns}
                                        />
                                    </li>
                                )
                        )}
                    </ul>
                </div>
            )}
            {bannerTitle && renderDesktopBanner()}
        </>
    );
}

export default NavigationItem;
